import { Global } from '@emotion/react';
import { useRouter } from 'next/router';
import { Box, Center, ChakraProvider, Divider } from '@chakra-ui/react';
import { useState } from 'react';
import { EstimateView } from './views/EstimateView';
import { OffsetView } from './views/OffsetView';
import {
  getMerchantConfig,
  IndustryEmittorType,
  IndustryIdType,
  INDUSTRY_IDS,
} from './configs/merchantConfigs';
import { createThemeByConfig } from './theme/theme';
import { Fonts } from './theme/components/Fonts';
import { EmissionCalculationInTonnes } from './configs/industryCalculationTemplate';
import { WidgetProvider } from './context/Widget-context';
import { CountrySelector } from './components/CountrySelector';
import { GeneralRegion } from './configs/countries';
import { Countries } from './calculation/calculations';
import { isNumber } from '../util/isNumber';

type Props = {
  locationId: string;
  themeName: string;
};

// TODO: we don't really want optional props in this type, we should rethink how we should do this
export type EstimationInput = {
  industryId: IndustryIdType;
  country?: string | undefined;
  employees: number;
  rooms?: number;
};

export type EstimationPresets = Partial<EstimationInput> & {
  industryId: IndustryIdType;
  industryIds: IndustryIdType[];
  emittorLabels: IndustryEmittorType;
  country: Countries;
  region: GeneralRegion;
};

export const parseEstimationInput = (
  formData: Record<string, string>,
): EstimationInput => {
  // @ts-ignore
  if (!INDUSTRY_IDS.includes(formData.industryId)) {
    throw Error(`Can't parse ${formData.industryId} as a valid industry id.`);
  }
  const industryId = formData.industryId as IndustryIdType;
  const country = formData.country;

  if (!isNumber(formData.employees)) {
    throw Error(
      `Can't parse ${formData.employees} as a valid employee number.`,
    );
  }
  const employees = parseFloat(formData.employees);

  const rooms = isNumber(formData.rooms)
    ? parseFloat(formData.rooms)
    : undefined;

  return {
    country,
    industryId,
    employees,
    rooms,
  };
};

export type EstimationSubmit = {
  emissionInTonnes: EmissionCalculationInTonnes;
  input: EstimationInput;
};

const App = (): JSX.Element => {
  const params = new URLSearchParams(document.location.search);
  const merchantCodeParam = params.get('merchantReferrerCode') || '';

  const merchantReferrerCodeFromParamsRaw = merchantCodeParam;
  const merchantReferrerCode =
    typeof merchantReferrerCodeFromParamsRaw === 'string'
      ? merchantReferrerCodeFromParamsRaw
      : '';

  const merchantConfig = getMerchantConfig(merchantReferrerCode);
  const theme = createThemeByConfig(merchantConfig);

  const [
    currentEstimation,
    setCurrentEstimation,
  ] = useState<EstimationSubmit | null>(null);
  const [countryCode, setCountryCode] = useState<Countries>(
    merchantConfig.estimationPresets.country,
  );
  const [regionKey, setRegionKey] = useState<GeneralRegion>(
    merchantConfig.estimationPresets.region,
  );

  const onSubmitEstimate = (estimation: EstimationSubmit) => {
    setCurrentEstimation(estimation);
  };

  const resetView = () => {
    setCurrentEstimation(null);
  };

  const onCountryChangeSelectorChange = (
    countryCode: Countries,
    regionKey: GeneralRegion,
  ) => {
    setCountryCode(countryCode);
    setRegionKey(regionKey);
  };

  return (
    <ChakraProvider theme={theme}>
      {/* <Center> */}
      <meta name="viewport" content="width=device-width, user-scalable=no"></meta>
      <GlobalStyles />
      <WidgetProvider countryCode={countryCode} regionKey={regionKey}>
        <Fonts />
        <Box
          m={{ base: '0', md: '10%' }}
          p={{ base: '5', md: '10' }}
          boxShadow="lg"
          bg="white"
          borderRadius="6px"
          // maxWidth="1140px"
        >
          {currentEstimation ? (
            <OffsetView currentEstimation={currentEstimation} />
          ) : (
            <EstimateView
              merchantConfig={merchantConfig}
              onSubmitEstimate={onSubmitEstimate}
              reset={resetView}
              countryCode={countryCode}
            />
          )}
          {/* DEV Note: this is the basic setup for the country/region selector */}
          <Divider mt={10} />
          <CountrySelector
            defaultCountry={countryCode}
            defaultRegion={regionKey}
            mt="10"
            onChange={onCountryChangeSelectorChange}
          />
        </Box>
      </WidgetProvider>
      {/* </Center> */}
    </ChakraProvider>
  );
};

export const GlobalStyles = () => (
  <Global
    styles={`
      abbr[title] {
        text-decoration: none;
        border: none;
      }
      html[data-whatintent='mouse'] *:focus,
      html[data-whatintent='touch'] *:focus
      {
        outline: none;
        box-shadow: none;
      }
     
      `}
  />
);

export default App;
